import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { API, http, usd } from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import { useStore } from "@/lib/store";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("checking");
  const { refresh } = useStore();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data?.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (data?.status === "expired") {
          setState("error");
          return;
        }
      } catch {
        if (tries > 3) {
          setState("error");
          return;
        }
      }
      if (tries < 12) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <main data-testid="order-success-container" className="grain relative min-h-screen bg-[#F2EAE1]">
      <div className="relative mx-auto max-w-3xl px-5 py-10 sm:px-8">
        <Logo />
        {state === "checking" && (
          <div data-testid="order-checking" className="mt-32 flex flex-col items-start gap-4">
            <Loader2 className="animate-spin text-[#C85A32]" />
            <p className="font-display text-3xl font-light">Confirming your payment…</p>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="order-error" className="mt-32">
            <AlertCircle className="text-[#C85A32]" />
            <p className="font-display mt-4 text-3xl font-light">We couldn't confirm this payment yet.</p>
            <p className="mt-3 text-[#4A4543]">If you were charged, refresh this page in a minute. Otherwise you can <a href="/" className="underline">return to the shop</a>.</p>
          </div>
        )}
        {state === "paid" && order && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="mt-20">
            <CheckCircle2 className="text-[#5E7153]" size={36} />
            <h1 className="font-display mt-6 text-5xl font-light leading-none tracking-tight sm:text-6xl">Welcome to the <span className="italic text-[#C85A32]">golden hour.</span></h1>
            <p className="mt-5 text-base text-[#4A4543] md:text-lg">Your Slowpour 01 ships from Alton, IL within 1–2 business days. A confirmation email with your recipe book and order link is on its way to your inbox.</p>
            <div data-testid="order-summary-card" className="mt-10 rounded-[28px] border border-[#E3D9CD] bg-[#FDFBF7] p-7">
              <div className="flex justify-between text-sm">
                <span className="eyebrow text-[#6E6661]">Order</span>
                <span data-testid="order-reference-id" className="font-mono-ghp">{order.order_ref}</span>
              </div>
              <dl className="mt-6 space-y-2 text-sm">
                <div className="flex justify-between"><dt>The Slowpour 01 × {order.quantity}</dt><dd>{usd(order.subtotal)}</dd></div>
                <div className="flex justify-between"><dt>Shipping</dt><dd>{usd(order.shipping)}</dd></div>
                <div className="flex justify-between"><dt>Sales tax</dt><dd>{usd(order.tax)}</dd></div>
                <div className="flex justify-between border-t border-[#E3D9CD] pt-3 font-semibold"><dt>Total paid</dt><dd data-testid="order-total">{usd(order.total)}</dd></div>
              </dl>
            </div>
            <div className="mt-6 flex flex-col items-start justify-between gap-6 rounded-[28px] bg-[#181615] p-7 text-[#F2EAE1] sm:flex-row sm:items-center">
              <div>
                <p className="eyebrow text-[#E0A030]">Your complimentary gift</p>
                <p className="font-display mt-2 text-2xl italic">The Recipe Almanac</p>
              </div>
              <a data-testid="download-full-recipe-pdf-button" href={order.access_token ? `${API}/orders/${order.access_token}/recipes` : `${API}/recipes/download?session_id=${sessionId}`} className="inline-flex items-center gap-2 rounded-full bg-[#E0A030] px-6 py-3.5 font-semibold text-[#181615] transition-[background-color] duration-300 hover:bg-[#F2EAE1]">
                <Download size={16} /> Download PDF
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              {order.access_token && (
                <a href={`/order/${order.access_token}`} data-testid="track-order-link" className="rounded-full border border-[#181615]/20 px-6 py-3 text-sm font-medium hover:bg-[#181615] hover:text-[#FDFBF7]">Track your order</a>
              )}
              <a href="/" data-testid="back-to-shop-link" className="text-sm underline">Back to the shop</a>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
